import React, { useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { toast } from "react-toastify"
import Table from "../../../../core/common/dataTable/index"
import CommonSelect from "../../../../core/common/commonSelect"
import { selectBranches, type Branch } from "../../../../core/data/redux/slice/branchesSlice"
import {
  getAllTeachers,
  getTeacherById,
  createTeacher,
  updateTeacher,
  deactivateTeacher,
  setCampusAccess,
  getClassSubjects,
  addAssignment,
  removeAssignment,
  setClassTeacherOf,
  type TeacherListItem,
  type TeacherProfile,
  type TeacherAssignment,
} from "../../../../service/teachers"
import { getAllRoles } from "../../../../service/roles-permission"
import { getAllSessions } from "../../../../service/sessions"
import { getAllClasses, getAllSections, type Class } from "../../../../service/classes"
import { getAllCourses, type Course } from "../../../../service/courses"

const TITLE_OPTIONS = ["Mr.", "Ms.", "Mrs.", "Miss", "Dr.", "Prof.", "Sir", "Madam"]

const EMPTY_FORM = {
  email: "",
  firstName: "",
  lastName: "",
  title: "",
  phoneNumber: "",
  password: "",
  role: "",
  status: "active" as "active" | "inactive",
  primarySubject: "",
  primaryStage: "",
  // Display-only -- auto-generated by the backend, never submitted.
  employeeId: "",
}

// ── Shared confirm modal (matches report-card-designer's pattern) ──────────
function ConfirmModal({ open, title, body, danger, onConfirm, onCancel }: {
  open: boolean; title: string; body: string; danger?: string
  onConfirm: () => void; onCancel: () => void
}) {
  if (!open) return null
  return (
    <>
      <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body text-center p-4">
              <span className="text-danger" style={{ fontSize: 40 }}><i className="ti ti-user-x" /></span>
              <h4 className="mt-2">{title}</h4>
              <p className="text-muted">{body}</p>
              <div className="d-flex justify-content-center gap-2 mt-2">
                <button className="btn btn-light" onClick={onCancel}>Cancel</button>
                <button className="btn btn-danger" onClick={onConfirm}>{danger || "Yes, Deactivate"}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </>
  )
}

const displayName = (t: { title?: string | null; firstName: string; lastName?: string }) =>
  `${t.title ? t.title + " " : ""}${t.firstName}${t.lastName ? " " + t.lastName : ""}`.trim()

const TeacherList = () => {
  const branches = useSelector(selectBranches) as Branch[]

  // ── List + filters ────────────────────────────────────────────────────
  const [teachers, setTeachers] = useState<TeacherListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [searchText, setSearchText] = useState("")
  const [filterRole, setFilterRole] = useState("")
  const [filterCampus, setFilterCampus] = useState("")
  const [roleOptions, setRoleOptions] = useState<{ _id: string; name: string }[]>([])

  // ── Master data for the assignment cascades ───────────────────────────
  const [sessions, setSessions] = useState<any[]>([])
  const [classes, setClasses] = useState<Class[]>([])
  // Separate from `classes` itself -- an empty array alone can't tell a
  // genuinely-empty result apart from a silently failed fetch. This was
  // reported live as the Class dropdown "sometimes" being empty with no
  // pattern: the fetch ran once on mount with no error handling, so any
  // transient failure (network blip, slow response right at page load)
  // left `classes` stuck at [] for the rest of the page session with zero
  // indication anything went wrong.
  const [classesFailed, setClassesFailed] = useState(false)
  const [courses, setCourses] = useState<Course[]>([])
  const [sessionSections, setSessionSections] = useState<Record<string, { _id: string; name: string }[]>>({})

  // ── Add/Edit modal ─────────────────────────────────────────────────────
  const [modal, setModal] = useState<null | "new" | "edit">(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState(EMPTY_FORM)
  const [campusIds, setCampusIds] = useState<string[]>([])
  const [existingAssignments, setExistingAssignments] = useState<TeacherAssignment[]>([])
  const [pendingAssignments, setPendingAssignments] = useState<any[]>([])
  const [removedIds, setRemovedIds] = useState<string[]>([])
  const [classTeacherIds, setClassTeacherIds] = useState<Set<string>>(new Set())
  const [saving, setSaving] = useState(false)

  const [draft, setDraft] = useState<any>({ sectionIds: [], courseIds: [] })
  const [draftSubjects, setDraftSubjects] = useState<Course[]>([])

  // ── View profile modal ─────────────────────────────────────────────────
  const [viewing, setViewing] = useState<TeacherProfile | null>(null)

  // ── Deactivate confirm ─────────────────────────────────────────────────
  const [pendingDeactivate, setPendingDeactivate] = useState<TeacherListItem | null>(null)
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)

  const classSystemId = (c: Class): string => {
    const s = c.system as any
    return s && typeof s === "object" ? s._id : s
  }

  // A campus can run several board systems at once (e.g. Naseerabad runs
  // both Matric and AKU), and the same class name ("Class IX") can exist as
  // a SEPARATE document per system. Plain {c.name} made those visually
  // identical in the dropdown -- picking "Class IX" always silently landed
  // on whichever one came first in the list, with no way to reach the
  // other. Building this off `branches` (already loaded, already carries
  // each system's name) instead of a new fetch.
  const systemNameById = useMemo(() => {
    const map = new Map<string, string>()
    branches.forEach((b: any) =>
      (b.systems || []).forEach((s: any) => {
        if (s && typeof s === "object" && s._id) map.set(s._id, s.name)
      })
    )
    return map
  }, [branches])

  const classLabel = (c: Class) => {
    const sysName = systemNameById.get(classSystemId(c))
    return sysName ? `${c.name} (${sysName})` : c.name
  }

  const fetchTeachers = async () => {
    setLoading(true)
    try {
      const res = await getAllTeachers({
        q: searchText || undefined,
        role: filterRole || undefined,
        campus: filterCampus || undefined,
      })
      setTeachers(res.data || [])
    } catch (err) {
      toast.error("Failed to load teachers")
      setTeachers([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchTeachers() }, [searchText, filterRole, filterCampus])

  // This modal assigns a teacher across campuses the admin isn't
  // necessarily currently viewing (top-bar campus != the modal's own
  // Campus picker), so classes must NOT be scoped to the admin's
  // currently-selected branch -- without `all`, the backend silently
  // restricts to req.branchFilter (the top-bar campus), which is why
  // Naseerabad/Ancholi/MPA Girls College showed an empty Class dropdown
  // whenever the top bar happened to be set to a different campus.
  //
  // Pulled out so a failed load can be retried without a full page reload --
  // previously this ran once on mount with a silent `.catch(() => {})`, so
  // any transient failure left the Class dropdown empty for the rest of the
  // page session with no error shown and no way to recover.
  const loadClasses = () => {
    setClassesFailed(false)
    getAllClasses({ limit: 500, all: "true" } as any)
      .then((res: any) => setClasses(res.classes || res.data || []))
      .catch(() => {
        setClasses([])
        setClassesFailed(true)
      })
  }

  useEffect(() => {
    // Master data loaded once -- assignment cascades filter these client-side.
    getAllRoles({ limit: 100 } as any)
      .then((res: any) => {
        const all = res.data || res.roles || []
        // Matches RMS's own four roles (teacher/head/data_manager/admin) --
        // "Principal" stands in for "head", "Data manager" was created to
        // match RMS 1:1 (see controllers/teachers.js STAFF_ROLE_NAMES).
        setRoleOptions(all.filter((r: any) => ["teacher", "Principal", "Data manager", "admin"].includes(r.name)))
      })
      .catch(() => setRoleOptions([]))
    getAllSessions({ limit: 200, all: true } as any).then((res: any) => setSessions(res.data || res.sessions || [])).catch(() => {})
    loadClasses()
    getAllCourses({}).then((res: any) => setCourses(res.data || [])).catch(() => {})
  }, [])

  // Sections for a given (session, campus) pair -- fetched lazily and
  // cached, since Class.sections is a lifetime-cumulative array shared
  // across every campus that offers the class (Class itself isn't
  // campus-scoped), and the real per-campus-per-session membership only
  // comes from a Section query filtered by BOTH (same gap fixed for Roll
  // Number -- see roll-number-research-findings memory -- plus branch,
  // since a class can be offered at several campuses at once).
  const sectionsCacheKey = (sessionId: string, branchId: string) => `${sessionId}::${branchId}`
  const ensureSessionSections = async (sessionId: string, branchId: string) => {
    if (!sessionId || !branchId) return
    const key = sectionsCacheKey(sessionId, branchId)
    if (sessionSections[key]) return
    try {
      const res = await getAllSections({ session: sessionId, branch: branchId })
      setSessionSections((s) => ({ ...s, [key]: res.data || res.sections || [] }))
    } catch {
      setSessionSections((s) => ({ ...s, [key]: [] }))
    }
  }

  const openNew = () => {
    setForm(EMPTY_FORM)
    setEditingId(null)
    setCampusIds([])
    setExistingAssignments([])
    setPendingAssignments([])
    setRemovedIds([])
    setClassTeacherIds(new Set())
    setDraft({ sectionIds: [], courseIds: [] })
    setIsPasswordVisible(false)
    setModal("new")
  }

  const openEdit = async (row: TeacherListItem) => {
    try {
      const res = await getTeacherById(row._id)
      const profile: TeacherProfile = res.data
      setForm({
        email: profile.email,
        firstName: profile.firstName,
        lastName: profile.lastName || "",
        title: profile.title || "",
        phoneNumber: profile.phoneNumber,
        password: "",
        role: profile.role?._id || "",
        status: profile.status,
        primarySubject: profile.primarySubject?._id || "",
        primaryStage: profile.primaryStage || "",
        employeeId: profile.employeeId || "",
      })
      setCampusIds((profile.campusAccess || []).filter(Boolean).map((c) => c._id))
      setExistingAssignments(profile.assignments || [])
      setPendingAssignments([])
      setRemovedIds([])
      setClassTeacherIds(new Set((profile.classTeacherSections || []).map((s) => s._id)))
      setDraft({ sectionIds: [], courseIds: [] })
      setIsPasswordVisible(false)
      setEditingId(row._id)
      setModal("edit")
    } catch (error: any) {
      console.error("Edit teacher load error:", error)
      toast.error(error?.response?.data?.message || error?.message || "Failed to load teacher")
    }
  }

  const openView = async (row: TeacherListItem) => {
    try {
      const res = await getTeacherById(row._id)
      setViewing(res.data)
    } catch (error: any) {
      console.error("View teacher load error:", error)
      toast.error(error?.response?.data?.message || error?.message || "Failed to load teacher")
    }
  }

  const toggleCampus = (id: string) =>
    setCampusIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]))

  const draftCampuses = useMemo(
    () => branches.filter((b) => campusIds.includes(b._id)),
    [branches, campusIds]
  )
  // Sessions are per-campus in our system (unlike RMS, where a session is
  // global) -- several campuses can have their own session that happens to
  // share the same display title (e.g. "Aug 2026-2027" once per campus).
  // So Campus has to be picked BEFORE Session, and Session's options are
  // filtered to that campus's own session docs (needs `all:true` from the
  // page-load fetch, which populates `.branch`).
  const sessionBranchId = (s: any): string => {
    const b = s?.branch
    return b && typeof b === "object" ? b._id : b
  }
  const draftSessions = useMemo(() => {
    if (!draft.campus) return []
    return sessions.filter((s) => sessionBranchId(s) === draft.campus)
  }, [sessions, draft.campus])

  const draftClasses = useMemo(() => {
    const campus = branches.find((b) => b._id === draft.campus)
    if (!campus) return []
    const allowedSystemIds = (campus.systems || []).map((s: any) => (typeof s === "object" ? s._id : s))
    return classes.filter((c) => allowedSystemIds.includes(classSystemId(c)))
  }, [classes, branches, draft.campus])

  const draftSections = useMemo(() => {
    if (!draft.class || !draft.session || !draft.campus) return []
    const cls = classes.find((c) => c._id === draft.class)
    if (!cls) return []
    const classSectionIds = new Set((cls.sections || []).map((s: any) => s._id))
    const sessionScoped = sessionSections[sectionsCacheKey(draft.session, draft.campus)] || []
    return sessionScoped.filter((sec) => classSectionIds.has(sec._id))
  }, [classes, draft.class, draft.session, draft.campus, sessionSections])

  // Reset dependents when an upstream draft field changes -- campus now
  // comes first in the cascade (see sessions note above), so picking a new
  // campus clears session/class/sections/subject; picking session or class
  // still only clears the fields below it.
  const setDraftField = (key: string, value: any) => {
    setDraft((s: any) => {
      const next = { ...s, [key]: value }
      if (key === "campus") { next.session = null; next.class = null; next.sectionIds = []; next.courseIds = [] }
      if (key === "class" || key === "session") { next.sectionIds = []; next.courseIds = [] }
      return next
    })
    if (key === "session") ensureSessionSections(value, draft.campus)
  }

  const toggleDraftCourse = (courseId: string) =>
    setDraft((s: any) => {
      const cur: string[] = s.courseIds || []
      return { ...s, courseIds: cur.includes(courseId) ? cur.filter((x) => x !== courseId) : [...cur, courseId] }
    })

  useEffect(() => {
    if (!draft.session || !draft.class || !draft.campus) { setDraftSubjects([]); return }
    getClassSubjects({ session: draft.session, class: draft.class, branch: draft.campus })
      .then((res: any) => setDraftSubjects(res.data || []))
      .catch(() => setDraftSubjects([]))
  }, [draft.session, draft.class, draft.campus])

  const draftReady =
    draft.session && draft.campus && draft.class &&
    (draft.courseIds?.length || 0) > 0 && (draft.sectionIds?.length || 0) > 0

  // Multi-select subjects × ticked sections -- one row per combination, so
  // allotting several subjects to this class/sections takes one "Add to
  // list" click instead of repeating the whole form per subject.
  const addDraft = () => {
    if (!draftReady) return
    const session = sessions.find((s) => s._id === draft.session)
    const campus = branches.find((b) => b._id === draft.campus)
    const cls = classes.find((c) => c._id === draft.class)
    const newRows: any[] = []
    draft.courseIds.forEach((cid: string) => {
      const course = courses.find((c) => c._id === cid)
      draft.sectionIds.forEach((sid: string) => {
        const sec = (sessionSections[sectionsCacheKey(draft.session, draft.campus)] || []).find((s) => s._id === sid)
        newRows.push({
          _localId: `${Date.now()}-${cid}-${sid}`,
          session: draft.session,
          branch: draft.campus,
          class: draft.class,
          section: sid,
          course: cid,
          sessionName: session?.name || session?.title,
          campusName: campus?.name,
          className: cls?.name,
          sectionName: sec?.name,
          courseName: course?.title,
        })
      })
    })
    setPendingAssignments((s) => [...s, ...newRows])
    // Keep campus/session/class so the next round (more subjects, or the
    // same subjects on different sections) doesn't need re-picking them.
    setDraft((s: any) => ({ ...s, sectionIds: [], courseIds: [] }))
  }

  const removeExisting = (id: string) => {
    setExistingAssignments((s) => s.filter((a) => a._id !== id))
    setRemovedIds((s) => [...s, id])
  }
  const removePending = (localId: string) =>
    setPendingAssignments((s) => s.filter((a) => a._localId !== localId))

  const candidateSections = useMemo(() => {
    const m = new Map<string, any>()
    const push = (a: any) => {
      const sid = a.section?._id || a.section
      if (!sid || m.has(sid)) return
      m.set(sid, {
        sectionId: sid,
        sectionName: a.section?.name || a.sectionName,
        className: a.class?.name || a.className,
      })
    }
    existingAssignments.forEach(push)
    pendingAssignments.forEach(push)
    return Array.from(m.values())
  }, [existingAssignments, pendingAssignments])

  const relevantStages = useMemo(() => {
    const allStages = new Set(classes.map((c) => c.stage).filter(Boolean) as string[])
    if (campusIds.length === 0) return Array.from(allStages).sort()
    const allowedSystemIds = draftCampuses.flatMap((c) =>
      (c.systems || []).map((s: any) => (typeof s === "object" ? s._id : s))
    )
    const stages = new Set(
      classes.filter((c) => allowedSystemIds.includes(classSystemId(c))).map((c) => c.stage).filter(Boolean) as string[]
    )
    return stages.size ? Array.from(stages).sort() : Array.from(allStages).sort()
  }, [classes, campusIds, draftCampuses])

  const relevantSubjects = useMemo(() => {
    const ids = new Set<string>()
    existingAssignments.forEach((a) => { const id = a.course?._id; if (id) ids.add(id) })
    pendingAssignments.forEach((a) => { if (a.course) ids.add(a.course) })
    if (!ids.size) return courses
    return courses.filter((c) => ids.has(c._id))
  }, [existingAssignments, pendingAssignments, courses])

  const toggleClassTeacher = (sid: string) =>
    setClassTeacherIds((cur) => {
      const next = new Set(cur)
      if (next.has(sid)) next.delete(sid); else next.add(sid)
      return next
    })

  const submit = async () => {
    if (!form.firstName || !form.phoneNumber || !form.role || (!editingId && (!form.email || !form.password))) {
      toast.error("First name, phone, role, and (for a new teacher) email + password are required")
      return
    }
    setSaving(true)
    try {
      let teacherId = editingId
      if (editingId) {
        await updateTeacher(editingId, {
          firstName: form.firstName,
          lastName: form.lastName || undefined,
          title: form.title || undefined,
          phoneNumber: form.phoneNumber,
          role: form.role,
          status: form.status,
          primarySubject: form.primarySubject || null,
          primaryStage: form.primaryStage || null,
          ...(form.password ? { password: form.password } : {}),
        })
      } else {
        const res = await createTeacher({
          email: form.email,
          firstName: form.firstName,
          lastName: form.lastName || undefined,
          title: form.title || undefined,
          phoneNumber: form.phoneNumber,
          password: form.password,
          role: form.role,
          status: form.status,
          primarySubject: form.primarySubject || null,
          primaryStage: form.primaryStage || null,
        })
        teacherId = res.data._id
      }

      await setCampusAccess(teacherId as string, campusIds)

      for (const id of removedIds) {
        await removeAssignment(teacherId as string, id)
      }
      for (const a of pendingAssignments) {
        await addAssignment(teacherId as string, {
          session: a.session, branch: a.branch, class: a.class, section: a.section, course: a.course,
        })
      }

      await setClassTeacherOf(teacherId as string, Array.from(classTeacherIds))

      toast.success(editingId ? "Teacher updated" : "Teacher created")
      setModal(null)
      fetchTeachers()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to save teacher")
    } finally {
      setSaving(false)
    }
  }

  const columns = [
    { title: "Name", dataIndex: "firstName", render: (_: any, r: TeacherListItem) => displayName(r) },
    { title: "Email", dataIndex: "email" },
    { title: "Role", dataIndex: "role", render: (r: any) => r?.name },
    {
      title: "Primary subject", dataIndex: "primarySubject",
      render: (v: any) => v?.title || <span className="text-muted">—</span>,
    },
    {
      title: "Primary stage", dataIndex: "primaryStage",
      render: (v: any) => v || <span className="text-muted">—</span>,
    },
    { title: "Employee ID", dataIndex: "employeeId", render: (v: any) => v || <span className="text-muted">—</span> },
    {
      title: "Class teacher", dataIndex: "classTeacherCount",
      render: (v: number) =>
        v > 0 ? <span className="badge bg-warning-transparent">★ {v} section{v === 1 ? "" : "s"}</span> : <span className="text-muted">—</span>,
    },
    {
      title: "Active", dataIndex: "status",
      render: (v: string) => (v === "active" ? "✓" : "—"),
    },
    {
      title: "Action", dataIndex: "action",
      render: (_: any, row: TeacherListItem) => (
        <div className="d-flex align-items-center gap-2">
          <button className="btn btn-sm btn-outline-secondary" onClick={() => openView(row)}>View</button>
          <button className="btn btn-sm btn-outline-primary" onClick={() => openEdit(row)}>Edit</button>
          <button className="btn btn-sm btn-outline-danger" onClick={() => setPendingDeactivate(row)}>Deactivate</button>
        </div>
      ),
    },
  ]

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div>
            <h4 className="mb-1">Teachers</h4>
            <p className="text-muted mb-0">Teachers, campus heads. Each has campus access + per-session class+subject assignments.</p>
          </div>
          <button className="btn btn-primary" onClick={openNew}>+ Add user</button>
        </div>

        <div className="row mb-3 g-2">
          <div className="col-md-4">
            <input
              type="search"
              className="form-control"
              placeholder="Search name or email…"
              autoComplete="off"
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <CommonSelect
              placeholder="Any role"
              // GET /api/teachers matches this against Role.name, not an _id --
              // unlike the Add/Edit form's role <select> below, which posts an
              // _id to create/update (a different, _id-based contract).
              options={roleOptions.map((r) => ({ value: r.name, label: r.name }))}
              value={roleOptions.filter((r) => r.name === filterRole).map((r) => ({ value: r.name, label: r.name }))[0] || null}
              onChange={(opt) => setFilterRole(opt?.value || "")}
            />
          </div>
          <div className="col-md-3">
            <CommonSelect
              placeholder="Any campus"
              options={branches.map((b) => ({ value: b._id, label: b.name }))}
              value={branches.filter((b) => b._id === filterCampus).map((b) => ({ value: b._id, label: b.name }))[0] || null}
              onChange={(opt) => setFilterCampus(opt?.value || "")}
            />
          </div>
        </div>

        <div className="card">
          <div className="card-body p-0">
            <Table
              columns={columns}
              dataSource={teachers}
              loading={loading}
              hidePagination
            />
          </div>
        </div>
      </div>

      {/* ── Add/Edit modal ──────────────────────────────────────────────── */}
      {modal && (
        <>
          <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{editingId ? `Edit teacher — ${displayName(form as any)}` : "New teacher"}</h5>
                  <button className="btn-close" onClick={() => setModal(null)} />
                </div>
                <div className="modal-body">
                  {/* Basic info */}
                  <h6 className="border-bottom pb-2 mb-3">Basic info</h6>
                  <div className="row g-3 mb-4">
                    <div className="col-md-2">
                      <label className="form-label">Title</label>
                      <select className="form-select" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}>
                        <option value="">—</option>
                        {TITLE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">First name</label>
                      <input className="form-control" value={form.firstName} onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Last name</label>
                      <input className="form-control" value={form.lastName} onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Employee ID</label>
                      <input className="form-control" value={form.employeeId} disabled placeholder="Assigned automatically" />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Email</label>
                      <input className="form-control" type="email" autoComplete="off" value={form.email} disabled={!!editingId} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Role</label>
                      <select className="form-select" value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}>
                        <option value="">Select role</option>
                        {roleOptions.map((r) => <option key={r._id} value={r._id}>{r.name}</option>)}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Phone</label>
                      <input className="form-control" value={form.phoneNumber} onChange={(e) => setForm((f) => ({ ...f, phoneNumber: e.target.value }))} />
                    </div>
                    <div className="col-md-8">
                      <label className="form-label">{editingId ? "Reset password (leave blank to keep)" : "Password"}</label>
                      <div className="pass-group">
                        <input
                          className="pass-input form-control"
                          type={isPasswordVisible ? "text" : "password"}
                          autoComplete="new-password"
                          value={form.password}
                          onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                        />
                        <span
                          className={`ti toggle-password ${isPasswordVisible ? "ti-eye" : "ti-eye-off"}`}
                          onClick={() => setIsPasswordVisible((v) => !v)}
                        />
                      </div>
                      <div className="form-text">Min 8 chars; upper, lower, digit</div>
                    </div>
                    <div className="col-md-4 d-flex align-items-center">
                      <div className="form-check form-switch mt-4">
                        <input className="form-check-input" type="checkbox" role="switch" checked={form.status === "active"} onChange={(e) => setForm((f) => ({ ...f, status: e.target.checked ? "active" : "inactive" }))} id="teacherActive" />
                        <label className="form-check-label" htmlFor="teacherActive">Active</label>
                      </div>
                    </div>
                  </div>

                  {/* Campus access */}
                  <h6 className="border-bottom pb-2 mb-1">Profile · Campuses</h6>
                  <p className="text-muted small mb-2">Operate at these campuses</p>
                  <div className="row mb-4">
                    {branches.map((b) => (
                      <div className="col-md-4 col-6" key={b._id}>
                        <div className="form-check">
                          <input className="form-check-input" type="checkbox" checked={campusIds.includes(b._id)} onChange={() => toggleCampus(b._id)} id={`campus-${b._id}`} />
                          <label className="form-check-label" htmlFor={`campus-${b._id}`}>{b.name}</label>
                        </div>
                      </div>
                    ))}
                    {branches.length === 0 && <p className="text-muted small">No campuses configured yet.</p>}
                  </div>

                  {/* Class assignments */}
                  <h6 className="border-bottom pb-2 mb-1">Profile · Classes</h6>
                  <p className="text-muted small mb-2">Per-session class + subject assignments</p>
                  {existingAssignments.length + pendingAssignments.length === 0 ? (
                    <div className="border rounded p-3 text-center text-muted small mb-3">No class assignments yet.</div>
                  ) : (
                    <div className="table-responsive mb-3">
                      <table className="table table-sm">
                        <thead><tr><th>Session</th><th>Campus</th><th>Class</th><th>Section</th><th>Subject</th><th></th></tr></thead>
                        <tbody>
                          {existingAssignments.map((a) => (
                            <tr key={a._id}>
                              <td>{a.session?.name || a.session?.title || "—"}</td>
                              <td>{a.branch?.name || "—"}</td>
                              <td>{a.class?.name}</td>
                              <td>{a.section?.name}</td>
                              <td>{a.course?.title}</td>
                              <td className="text-end"><button className="btn btn-sm btn-link text-danger" onClick={() => removeExisting(a._id)}>Remove</button></td>
                            </tr>
                          ))}
                          {pendingAssignments.map((a) => (
                            <tr key={a._localId} className="table-success">
                              <td>{a.sessionName || "—"}</td>
                              <td>{a.campusName || "—"}</td>
                              <td>{a.className}</td>
                              <td>{a.sectionName}</td>
                              <td>{a.courseName}</td>
                              <td className="text-end"><button className="btn btn-sm btn-link text-danger" onClick={() => removePending(a._localId)}>Discard</button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="border rounded bg-light p-3 mb-4">
                    <h6 className="text-uppercase text-muted small mb-2">Add assignment</h6>
                    <div className="row g-2 mb-2">
                      <div className="col-md-4">
                        <label className="form-label small">Campus</label>
                        <select className="form-select form-select-sm" value={draft.campus || ""} disabled={campusIds.length === 0} onChange={(e) => setDraftField("campus", e.target.value)}>
                          <option value="">— Select campus —</option>
                          {draftCampuses.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
                        </select>
                        {campusIds.length === 0 && <div className="form-text">Tick campuses in Profile · Campuses first</div>}
                      </div>
                      <div className="col-md-4">
                        {/* Sessions are per-campus in our system (unlike RMS) --
                            pick campus first so this only lists that campus's
                            own sessions, never another campus's same-named one. */}
                        <label className="form-label small">Session</label>
                        <select className="form-select form-select-sm" value={draft.session || ""} disabled={!draft.campus} onChange={(e) => setDraftField("session", e.target.value)}>
                          <option value="">— Select session —</option>
                          {draftSessions.map((s) => <option key={s._id} value={s._id}>{s.name || s.title}</option>)}
                        </select>
                        {!draft.campus && <div className="form-text">Pick campus first</div>}
                      </div>
                      <div className="col-md-4">
                        <label className="form-label small">Class</label>
                        <select className="form-select form-select-sm" value={draft.class || ""} disabled={!draft.campus} onChange={(e) => setDraftField("class", e.target.value)}>
                          <option value="">— Select class —</option>
                          {draftClasses.map((c) => <option key={c._id} value={c._id}>{classLabel(c)}</option>)}
                        </select>
                        {!draft.campus && <div className="form-text">Pick campus first</div>}
                        {classesFailed && (
                          <div className="form-text text-danger">
                            Failed to load classes.{" "}
                            <button type="button" className="btn btn-link btn-sm p-0" onClick={loadClasses}>Retry</button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="mb-2">
                      {/* Checkbox list, not a single-select -- a teacher commonly
                          teaches several subjects in the same class, and picking
                          one subject at a time (re-filling campus/session/class
                          each round) was the exact complaint reported live. */}
                      <div className="d-flex align-items-center justify-content-between">
                        <label className="form-label small mb-1">
                          Subjects {draft.courseIds?.length > 0 && <span className="text-muted">({draft.courseIds.length} selected)</span>}
                        </label>
                        {draftSubjects.length > 0 && (draft.courseIds?.length || 0) > 0 && (
                          <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none" onClick={() => setDraft((s: any) => ({ ...s, courseIds: [] }))}>
                            Clear
                          </button>
                        )}
                      </div>
                      {draftSubjects.length > 0 ? (
                        <div className="border rounded bg-white p-2">
                          <div className="row">
                            {draftSubjects.map((c) => (
                              <div className="col-md-3 col-6" key={c._id}>
                                <div className="form-check">
                                  <input
                                    className="form-check-input" type="checkbox"
                                    checked={draft.courseIds?.includes(c._id) || false}
                                    onChange={() => toggleDraftCourse(c._id)}
                                    id={`course-${c._id}`}
                                  />
                                  <label className="form-check-label" htmlFor={`course-${c._id}`}>{c.title}</label>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="border border-dashed rounded p-2 text-muted small">
                          {(!draft.class || !draft.session)
                            ? "Pick campus + session + class to load subjects"
                            : "No subjects linked to this class — set them up in Course Structures first"}
                        </div>
                      )}
                    </div>
                    <div className="mb-2">
                      <label className="form-label small">Sections</label>
                      {draftSections.length > 0 ? (
                        <div className="border rounded bg-white p-2">
                          <div className="row">
                            {draftSections.map((sec) => (
                              <div className="col-md-3 col-6" key={sec._id}>
                                <div className="form-check">
                                  <input
                                    className="form-check-input" type="checkbox"
                                    checked={draft.sectionIds?.includes(sec._id) || false}
                                    onChange={() => setDraft((s: any) => {
                                      const cur = s.sectionIds || []
                                      return { ...s, sectionIds: cur.includes(sec._id) ? cur.filter((x: string) => x !== sec._id) : [...cur, sec._id] }
                                    })}
                                    id={`sec-${sec._id}`}
                                  />
                                  <label className="form-check-label" htmlFor={`sec-${sec._id}`}>{sec.name}</label>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="border border-dashed rounded p-2 text-muted small">
                          {!draft.campus || !draft.class || !draft.session ? "Pick campus + session + class to load sections" : "No sections for this class this session at this campus"}
                        </div>
                      )}
                    </div>
                    <div className="d-flex justify-content-end">
                      <button className="btn btn-sm btn-dark" disabled={!draftReady} onClick={addDraft}>+ Add to list</button>
                    </div>
                  </div>

                  {/* Class teacher of */}
                  <h6 className="border-bottom pb-2 mb-1">Class teacher of</h6>
                  <p className="text-muted small mb-2">Tick the sections this user is the homeroom / form teacher of</p>
                  {candidateSections.length === 0 ? (
                    <div className="border rounded p-3 text-center text-muted small mb-4">
                      Add class assignments above first — class-teacher duty can only be set on sections this user already teaches.
                    </div>
                  ) : (
                    <div className="row mb-4">
                      {candidateSections.map((row) => (
                        <div className="col-md-4 col-6" key={row.sectionId}>
                          <div className="form-check">
                            <input className="form-check-input" type="checkbox" checked={classTeacherIds.has(row.sectionId)} onChange={() => toggleClassTeacher(row.sectionId)} id={`ct-${row.sectionId}`} />
                            <label className="form-check-label" htmlFor={`ct-${row.sectionId}`}>{row.className} · {row.sectionName}</label>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Primary stage/subject */}
                  <h6 className="border-bottom pb-2 mb-1">Profile · Stage and subject</h6>
                  <p className="text-muted small mb-2">Pick the canonical stage + subject for this teacher</p>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Primary stage</label>
                      <select className="form-select" value={form.primaryStage} onChange={(e) => setForm((f) => ({ ...f, primaryStage: e.target.value }))}>
                        <option value="">—</option>
                        {relevantStages.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Primary subject</label>
                      <select className="form-select" value={form.primarySubject} onChange={(e) => setForm((f) => ({ ...f, primarySubject: e.target.value }))}>
                        <option value="">—</option>
                        {relevantSubjects.map((c) => <option key={c._id} value={c._id}>{c.title}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-light" onClick={() => setModal(null)}>Cancel</button>
                  <button className="btn btn-primary" disabled={saving} onClick={submit}>{saving ? "Saving…" : "Save"}</button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}

      {/* ── View profile modal ──────────────────────────────────────────── */}
      {viewing && (
        <>
          <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Profile — {displayName(viewing as any)}</h5>
                  <button className="btn-close" onClick={() => setViewing(null)} />
                </div>
                <div className="modal-body">
                  <h6 className="mb-2">Basic info</h6>
                  <dl className="row small mb-4">
                    <dt className="col-sm-4">Email</dt><dd className="col-sm-8">{viewing.email}</dd>
                    <dt className="col-sm-4">Role</dt><dd className="col-sm-8">{viewing.role?.name}</dd>
                    <dt className="col-sm-4">Employee ID</dt><dd className="col-sm-8">{viewing.employeeId || "—"}</dd>
                    <dt className="col-sm-4">Phone</dt><dd className="col-sm-8">{viewing.phoneNumber}</dd>
                    <dt className="col-sm-4">Primary subject</dt><dd className="col-sm-8">{viewing.primarySubject?.title || "—"}</dd>
                    <dt className="col-sm-4">Primary stage</dt><dd className="col-sm-8">{viewing.primaryStage || "—"}</dd>
                    <dt className="col-sm-4">Active</dt><dd className="col-sm-8">{viewing.status === "active" ? "✓" : "—"}</dd>
                  </dl>

                  <h6 className="mb-2">Campus access</h6>
                  {(!viewing.campusAccess || viewing.campusAccess.filter(Boolean).length === 0) ? (
                    <p className="text-muted small">No campuses assigned.</p>
                  ) : (
                    <div className="mb-4 d-flex flex-wrap gap-2">
                      {viewing.campusAccess.filter(Boolean).map((c) => <span key={c._id} className="badge bg-light text-dark border">{c.name}</span>)}
                    </div>
                  )}

                  <h6 className="mb-2">Class teacher of ({viewing.classTeacherSections?.length || 0})</h6>
                  {(!viewing.classTeacherSections || viewing.classTeacherSections.length === 0) ? (
                    <p className="text-muted small">Not a class teacher of any section.</p>
                  ) : (
                    <div className="mb-4 d-flex flex-wrap gap-2">
                      {viewing.classTeacherSections.map((s) => (
                        <span key={s._id} className="badge bg-warning-transparent">★ {s.className || ""} · {s.name}</span>
                      ))}
                    </div>
                  )}

                  <h6 className="mb-2">Class assignments ({viewing.assignments?.length || 0})</h6>
                  {(!viewing.assignments || viewing.assignments.length === 0) ? (
                    <p className="text-muted small">No class assignments yet.</p>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-sm">
                        <thead><tr><th>Session</th><th>Campus</th><th>Class</th><th>Section</th><th>Subject</th></tr></thead>
                        <tbody>
                          {viewing.assignments.map((a) => (
                            <tr key={a._id}>
                              <td>{a.session?.name || a.session?.title || "—"}</td>
                              <td>{a.branch?.name || "—"}</td>
                              <td>{a.class?.name}</td>
                              <td>{a.section?.name}</td>
                              <td>{a.course?.title}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button className="btn btn-light" onClick={() => setViewing(null)}>Close</button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}

      <ConfirmModal
        open={!!pendingDeactivate}
        title="Deactivate teacher?"
        body={pendingDeactivate ? `${displayName(pendingDeactivate)} will no longer be able to sign in.` : ""}
        onCancel={() => setPendingDeactivate(null)}
        onConfirm={async () => {
          if (!pendingDeactivate) return
          try {
            await deactivateTeacher(pendingDeactivate._id)
            toast.success("Teacher deactivated")
            setPendingDeactivate(null)
            fetchTeachers()
          } catch {
            toast.error("Failed to deactivate")
          }
        }}
      />
    </div>
  )
}

export default TeacherList
